import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { LayoutDashboard, Users, School, ClipboardCheck, FileText, LogOut, User, DollarSign, Shield, AlertTriangle, Clock, Menu, X, Trophy, UserPlus, CreditCard, Settings, Bot } from "lucide-react";
import NotificacoesBell from "./NotificacoesBell";


export default function Layout({ children, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [escolaData, setEscolaData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Detectar se é login auxiliar
  const username = localStorage.getItem('username');
  const isAuxiliar = username && username.includes('@'); // Login auxiliar usa email

  useEffect(() => {
    if (!isAuxiliar) {
      fetchEscolaData();
    }
  }, [isAuxiliar]);

  const fetchEscolaData = async () => {
    try {
      const response = await api.get('/perfil/escola');
      setEscolaData(response.data);
    } catch (error) {
      console.error("Erro ao carregar dados da escola");
    }
  };

  // Se for login auxiliar, mostrar apenas Chamada
  const navItems = isAuxiliar ? [
    { path: "/chamada", label: "Chamada", icon: ClipboardCheck },
  ] : [
    { path: "/", label: "Dashboard", icon: LayoutDashboard },
    { path: "/alunos", label: "Alunos", icon: Users },
    { path: "/pre-matriculas", label: "Pré-Matrículas", icon: UserPlus },
    { path: "/turmas", label: "Turmas", icon: School },
    { path: "/chamada", label: "Chamada", icon: ClipboardCheck },
    { path: "/mensalidades", label: "Financeiro", icon: DollarSign },
    { path: "/configuracoes/cobranca-automatica", label: "Cobrança Auto", icon: Bot },
    { path: "/configuracoes/pagamento", label: "Pagamentos", icon: CreditCard },
    { path: "/arena", label: "Arena", icon: Trophy },
    { path: "/relatorios", label: "Relatórios", icon: FileText },
  ];

  if (!isAuxiliar && escolaData?.is_admin) {
    navItems.push({ path: "/admin", label: "Administração", icon: Shield });
  }

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Redirecionar login auxiliar para /chamada
  useEffect(() => {
    if (isAuxiliar && location.pathname === "/") {
      navigate("/chamada", { replace: true });
    }
  }, [isAuxiliar, location.pathname, navigate]);

  // Fechar menu mobile ao mudar de rota
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 gap-2">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center shrink-0">
                <span className="text-xl sm:text-2xl">⚽</span>
              </div>
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-bold truncate" style={{ fontFamily: 'Space Grotesk' }}>
                  {escolaData?.nome_escola || 'GEE'}
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-500 truncate hidden sm:block">Sistema de Gestão Escolar</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              {/* Menu Hamburguer Mobile */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden h-9 w-9 p-0"
                data-testid="mobile-menu-toggle"
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>

              {/* Botões Desktop - esconder Perfil para auxiliar */}
              {!isAuxiliar && (
                <Button
                  data-testid="perfil-button"
                  variant="outline"
                  onClick={() => navigate('/perfil')}
                  className="hidden sm:flex items-center gap-2"
                >
                  <User className="w-4 h-4" />
                  <span className="hidden md:inline">Perfil</span>
                </Button>
              )}
              <NotificacoesBell />
              <Button
                data-testid="logout-button"
                variant="outline"
                onClick={onLogout}
                className="hidden sm:flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Sair</span>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Banner de Período de Teste - Visível em TODAS as páginas */}
      {escolaData && !escolaData.is_admin && !escolaData.is_active && escolaData.trial_days_remaining >= 0 && (
        <div className={`border-b-4 ${
          escolaData.trial_days_remaining <= 2 ? 'bg-red-100 border-red-500' : 'bg-blue-100 border-blue-500'
        }`}>
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6">
            <div className={`rounded-lg border-2 p-3 sm:p-6 ${
              escolaData.trial_days_remaining <= 2 
                ? 'border-red-400 bg-red-50' 
                : 'border-blue-400 bg-blue-50'
            }`}>
              <div className="flex items-start gap-3 sm:gap-4">
                <Clock className={`h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 mt-1 ${
                  escolaData.trial_days_remaining <= 2 ? 'text-red-600' : 'text-blue-600'
                }`} />
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-base sm:text-2xl mb-2 sm:mb-3 ${
                    escolaData.trial_days_remaining <= 2 ? 'text-red-900' : 'text-blue-900'
                  }`}>
                    ⏱️ Funcionamento Permitido por Uma Semana
                  </h3>
                  {escolaData.trial_expired ? (
                    <div className="space-y-2">
                      <p className="font-semibold text-sm sm:text-lg text-red-800">
                        ❌ Seu período experimental de 7 dias expirou.
                      </p>
                      <p className="text-xs sm:text-base text-red-700">
                        <strong>Todos os serviços estão bloqueados.</strong> Entre em contato com o administrador mestre 
                        para aprovação e liberação permanente do acesso.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      <p className={`font-bold text-base sm:text-xl ${
                        escolaData.trial_days_remaining <= 2 ? 'text-red-800' : 'text-blue-800'
                      }`}>
                        ⏳ Restam <span className="text-2xl sm:text-3xl">{escolaData.trial_days_remaining}</span> dia{escolaData.trial_days_remaining !== 1 ? 's' : ''} do período experimental
                      </p>
                      <p className={`text-xs sm:text-base ${escolaData.trial_days_remaining <= 2 ? 'text-red-700' : 'text-blue-700'}`}>
                        Após {escolaData.trial_days_remaining} dia{escolaData.trial_days_remaining !== 1 ? 's' : ''}, 
                        <strong> todos os serviços serão bloqueados</strong> até que o administrador mestre aprove sua conta.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Assinatura Próxima do Vencimento */}
      {escolaData && !escolaData.is_admin && escolaData.subscription_days_remaining >= 0 && escolaData.subscription_days_remaining <= 7 && (
        <div className={`border-b-4 ${
          escolaData.subscription_days_remaining <= 2 ? 'bg-red-100 border-red-500' : 'bg-orange-100 border-orange-500'
        }`}>
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6">
            <div className={`rounded-lg border-2 p-3 sm:p-6 ${
              escolaData.subscription_days_remaining <= 2
                ? 'border-red-400 bg-red-50'
                : 'border-orange-400 bg-orange-50'
            }`}>
              <div className="flex items-start gap-3 sm:gap-4">
                <AlertTriangle className={`h-6 w-6 sm:h-8 sm:w-8 flex-shrink-0 mt-1 ${
                  escolaData.subscription_days_remaining <= 2 ? 'text-red-600' : 'text-orange-600'
                }`} />
                <div className="flex-1 min-w-0">
                  <h3 className={`font-bold text-base sm:text-2xl mb-2 sm:mb-3 ${
                    escolaData.subscription_days_remaining <= 2 ? 'text-red-900' : 'text-orange-900'
                  }`}>
                    {escolaData.subscription_expired ? '🚫 Assinatura Expirada' : '⚠️ Assinatura Próxima do Vencimento'}
                  </h3>
                  {escolaData.subscription_expired ? (
                    <div className="space-y-2">
                      <p className="font-semibold text-sm sm:text-lg text-red-800">
                        Sua assinatura {escolaData.subscription_plan} expirou.
                      </p>
                      <p className="text-xs sm:text-base text-red-700">
                        <strong>Todos os serviços estão bloqueados.</strong> Entre em contato para renovar: 
                        <a href="https://wa.me/5562996379383" target="_blank" rel="noopener noreferrer" className="ml-1 underline font-bold whitespace-nowrap">
                          (62) 99637-9383
                        </a>
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      <p className={`font-bold text-base sm:text-xl ${
                        escolaData.subscription_days_remaining <= 2 ? 'text-red-800' : 'text-orange-800'
                      }`}>
                        ⏳ Sua assinatura <span className="uppercase">{escolaData.subscription_plan}</span> expira em <span className="text-2xl sm:text-3xl">{escolaData.subscription_days_remaining}</span> dia{escolaData.subscription_days_remaining !== 1 ? 's' : ''}
                      </p>
                      <p className={`text-xs sm:text-base ${escolaData.subscription_days_remaining <= 2 ? 'text-red-700' : 'text-orange-700'}`}>
                        Renove agora para não perder o acesso. Contato: 
                        <a href="https://wa.me/5562996379383" target="_blank" rel="noopener noreferrer" className="ml-1 underline font-bold whitespace-nowrap">
                          (62) 99637-9383
                        </a>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Mobile Overlay usando Portal */}
      {mobileMenuOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden" onClick={() => setMobileMenuOpen(false)}>
          <div className="fixed left-0 top-0 bottom-0 w-64 bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b">
              <h2 className="font-bold text-lg">Menu</h2>
            </div>
            <div className="p-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}>
                    <Button
                      variant={active ? "default" : "ghost"}
                      className={`w-full justify-start ${
                        active
                          ? "bg-gradient-to-r from-green-500 to-blue-500 text-white"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
              <div className="pt-4 mt-4 border-t space-y-2">
                {!isAuxiliar && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigate('/perfil');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full justify-start"
                  >
                    <User className="w-5 h-5 mr-3" />
                    Perfil
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    onLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full justify-start text-red-600"
                >
                  <LogOut className="w-5 h-5 mr-3" />
                  Sair
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4 md:py-8">
        <div className="flex gap-6">
          {/* Sidebar Desktop */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm p-4 space-y-1 sticky top-24">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link key={item.path} to={item.path}>
                    <Button
                      data-testid={`nav-${item.label.toLowerCase()}`}
                      variant={active ? "default" : "ghost"}
                      className={`w-full justify-start ${
                        active
                          ? "bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-600 hover:to-blue-600"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </Button>
                  </Link>
                );
              })}
            </div>
          </aside>

          <main className="flex-1 min-w-0">{children}</main>
        </div>
      </div>
    </div>
  );
}
